import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { CallbackModal } from '../components/CallbackModal';
import { Accordion } from '../components/ui/Accordion';
import { MapPin, Star, Languages, Calendar, MessageCircle, Phone, CheckCircle2, Plus, Minus, AlertCircle, Clock, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const TELEGRAM_URL = 'https://t.me/dentico_clinic';
function TelegramIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className} data-id="element-1863" data-mp="o5y4alsqj4zh">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-1864" data-mp="23n87n8hqf0f8" />
    </svg>;
}
export function EmergencyDentistLondonRuPage() {
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const faqs = [{
    q: 'Что делать, если сильно болит зуб?',
    a: 'Как можно скорее свяжитесь с нами для неотложного осмотра. Мы определим причину боли и предложим лечение. Не грейте больное место!'
  }, {
    q: 'Принимаете ли вы срочно в тот же день?',
    a: 'Да, мы всегда стараемся найти время для пациентов с острой болью в тот же день. Напишите в WhatsApp/Telegram или позвоните.'
  }, {
    q: 'Сколько стоит экстренный прием?',
    a: 'Стоимость зависит от необходимых процедур и диагностики. Экстренная консультация — £60. После осмотра мы объясним варианты лечения и цены.'
  }, {
    q: 'Какие симптомы считаются неотложными?',
    a: 'Сильная зубная боль, отек лица или десен, гной, повышенная температура, боль при глотании, травма зуба (скол, вывих), кровотечение после удаления.'
  }, {
    q: 'Что делать, если выбили зуб?',
    a: 'Найдите зуб, держите его за коронку (не за корень!), промойте водой, если грязный. Попробуйте вставить обратно в лунку или положите в молоко/слюну. Обратитесь к врачу в течение 30-60 минут!'
  }, {
    q: 'Работаете ли вы в выходные?',
    a: 'Мы работаем с понедельника по субботу. В неотложных случаях напишите нам в WhatsApp, мы попробуем помочь или посоветовать.'
  }, {
    q: 'Можно ли получить помощь на русском?',
    a: 'Да, в Dentico Clinic возможно общение на русском языке. Вы сможете точно описать свои симптомы.'
  }, {
    q: 'Где вы находитесь?',
    a: '360–364 City Road, London EC1V 2PY (Angel / Islington), рядом с Angel Station.'
  }];
  // Schemas
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a
      }
    }))
  };
  const procedureSchema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalProcedure',
    name: 'Emergency Dental Care',
    description: 'Urgent dental treatment for pain relief, trauma, or infection.',
    procedureType: 'http://schema.org/SurgicalProcedure',
    provider: {
      '@type': 'Dentist',
      name: 'Dentico Clinic',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '360-364 City Road',
        addressLocality: 'London',
        postalCode: 'EC1V 2PY',
        addressCountry: 'GB'
      }
    }
  };
  return <Layout data-id="element-1865" data-mp="1tb8b8pow5kt1">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-1866" data-mp="1tc03prn6z2f3" />

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} data-id="element-1867" data-mp="1k9ijfir3kjuq" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(procedureSchema)
    }} data-id="element-1868" data-mp="1fbz2uc6xfuzm" />

      {/* 1. HERO SECTION (Urgent styling) */}
      <section className="relative bg-white overflow-hidden" data-id="element-1869" data-mp="27zv13lc59uyi">
        <div className="absolute inset-0 bg-red-50/50 -skew-y-3 origin-top-left transform translate-y-20" data-id="element-1870" data-mp="238352va7yx0z" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-20 lg:pb-24 relative" data-id="element-1871" data-mp="1yabeq5p83iyw">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-1872" data-mp="2vpz35o9opmne">
            <div className="space-y-6" data-id="element-1873" data-mp="2qyh1sz3r1k1w">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-100 text-red-800 text-sm font-medium animate-pulse" data-id="element-1874" data-mp="2qxfe9v5jr53a">
                <AlertCircle className="w-4 h-4" data-id="element-1875" data-mp="2ybpy5blemdx2" />
                Неотложная помощь
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight tracking-tight" data-id="element-1876" data-mp="387jcn0ioohhh">
                Неотложная стоматологическая помощь в Лондоне –{' '}
                <span className="text-red-600" data-id="element-1877" data-mp="3d49qriaxh309">Dentico Clinic</span>
                <br data-id="element-1878" data-mp="3h7oi354bh5t0" />
                <span className="text-2xl sm:text-3xl font-medium text-gray-500 mt-2 block" data-id="element-1879" data-mp="3h650nb6xcopn">
                  (Angel, Islington)
                </span>
              </h1>

              <p className="text-lg text-gray-600 max-w-lg leading-relaxed" data-id="element-1880" data-mp="3qzc76zdeiarb">
                Острая боль? Сломанный зуб? Отек? Мы принимаем пациентов с
                острой болью в тот же день. Консультация на русском языке.
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-3 text-sm font-medium text-[#2F4A3F]" data-id="element-1881" data-mp="8tfwwi44jhav">
                <div className="flex items-center gap-1.5 bg-[#F8F5F0] px-3 py-1.5 rounded-lg border border-[#D8C4A8] shadow-sm" data-id="element-1882" data-mp="25s90hqy6fsy0">
                  <Clock className="w-4 h-4 text-[#2F4A3F]" data-id="element-1883" data-mp="20vikt4qaazd4" />
                  Срочный приём
                </div>
                <div className="flex items-center gap-1.5 bg-[#F8F5F0] px-3 py-1.5 rounded-lg border border-[#D8C4A8] shadow-sm" data-id="element-1884" data-mp="2emngjvqj77i6">
                  <MapPin className="w-4 h-4 text-[#2F4A3F]" data-id="element-1885" data-mp="2oj9x48btrcee" />
                  Angel, EC1V
                </div>
                <div className="flex items-center gap-1.5 bg-[#F8F5F0] px-3 py-1.5 rounded-lg border border-[#D8C4A8] shadow-sm" data-id="element-1886" data-mp="2jkyp8353bbyt">
                  <Languages className="w-4 h-4 text-[#2F4A3F]" data-id="element-1887" data-mp="2xix2t6bl74d9" />
                  Русский язык
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4" data-id="element-1888" data-mp="2tgsb4d4p8ht9">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-1889" data-mp="136p89u36gpi0">
                  <Button variant="whatsapp" size="lg" className="w-full gap-2" data-id="element-1890" data-mp="p16s0gk1oqan">
                    <MessageCircle className="w-5 h-5" data-id="element-1891" data-mp="t0yv4dqo648m" />
                    Написать в WhatsApp
                  </Button>
                </a>
                <a href="tel:+447345197820" className="w-full sm:w-auto" data-id="element-1892" data-mp="f912ddh9mkym">
                  <Button variant="secondary" size="lg" className="w-full gap-2" data-id="element-1893" data-mp="k83yu9j6jiuz">
                    <Phone className="w-5 h-5" data-id="element-1894" data-mp="ac0wb2kf1i7i" />
                    +44 7345 197820
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative" data-id="element-1895" data-mp="1b2sak9z9aox">
              <div className="absolute inset-0 bg-red-100 rounded-3xl transform rotate-3 blur-sm opacity-30" data-id="element-1896" data-mp="1c43qpuwnz76"></div>
              <img src="https://images.unsplash.com/photo-1598256989800-fe5f95da9787?auto=format&fit=crop&w=1200&q=80" alt="Неотложная стоматология Лондон" className="relative rounded-3xl shadow-2xl object-cover h-[300px] sm:h-[400px] lg:h-[500px] w-full grayscale-[20%]" data-id="element-1897" data-mp="1qrbuxyxunpf0" />
            </div>
          </div>
        </div>
      </section>

      {/* 2. WHEN TO SEEK HELP */}
      <section className="py-16 bg-white" data-id="element-1898" data-mp="2uhd7cyp6u2kg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1899" data-mp="2qixon7053gcu">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-1900" data-mp="2lj1mldb05yzx">
            Когда нужна неотложная помощь?
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1901" data-mp="2gix2zeesso5h">
            {[{
            title: 'Сильная зубная боль',
            desc: 'Боль, которая не проходит от обезболивающих, пульсирующая или ночная боль.'
          }, {
            title: 'Отек лица',
            desc: 'Припухлость щеки или десны может свидетельствовать о серьезной инфекции.'
          }, {
            title: 'Травма зуба',
            desc: 'Сломанный, сколотый или выбитый зуб в результате удара или падения.'
          }, {
            title: 'Потеря пломбы/коронки',
            desc: 'Если выпала пломба или коронка, и зуб стал чувствительным или острым.'
          }, {
            title: 'Кровотечение',
            desc: 'Кровотечение из десны после удаления зуба, которое не останавливается.'
          }, {
            title: 'Боль при глотании',
            desc: 'Затрудненное глотание или дыхание — это признак опасного распространения инфекции.'
          }].map((item, idx) => <Card key={idx} className="p-6 border-l-4 border-l-red-500" data-id="element-1902" data-mp="1iqfjnqizygt4" data-mpi={idx}>
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2" data-id="element-1903" data-mp="1iockogzv9dc9">
                  <AlertCircle className="w-5 h-5 text-red-500" data-id="element-1904" data-mp="2p69jmpfljae5" />
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-1905" data-mp="2u7fd4ph2z1oq">{item.desc}</p>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 3. FIRST AID */}
      <section className="py-16 bg-red-50" data-id="element-1906" data-mp="k1jfp4c6zi8x">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1907" data-mp="otucvzfjspxs">
          <div className="text-center mb-10" data-id="element-1908" data-mp="oumatgwmazr8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1909" data-mp="ttfe6gi6xado">
              Что делать до визита к врачу?
            </h2>
            <p className="text-lg text-gray-600" data-id="element-1910" data-mp="3sburhxflztkd">
              Советы первой помощи, пока вы ждете приема.
            </p>
          </div>
          <div className="space-y-4" data-id="element-1911" data-mp="1633p4pb0t0q">
            <div className="bg-white p-6 rounded-xl shadow-sm" data-id="element-1912" data-mp="633a5gq2fxsd">
              <h3 className="font-bold text-lg mb-2 text-gray-900" data-id="element-1913" data-mp="65pg5bxb658h">
                При зубной боли
              </h3>
              <p className="text-gray-600" data-id="element-1914" data-mp="1hirv62jremqj">
                Примите обезболивающее (ибупрофен или парацетамол), прополощите
                рот теплой водой. Не прикладывайте аспирин к десне и не грейте
                больное место!
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm" data-id="element-1915" data-mp="3648gorp8xjtf">
              <h3 className="font-bold text-lg mb-2 text-gray-900" data-id="element-1916" data-mp="321u0yggvj06d">
                Если выбили зуб
              </h3>
              <p className="text-gray-600" data-id="element-1917" data-mp="2x7zkdlwkf1bi">
                Держите зуб только за коронку (белую часть). Если грязный —
                промойте молоком или водой. Попробуйте вставить обратно в лунку
                или положите в молоко/слюну. Время критично — у вас есть 30-60
                минут!
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm" data-id="element-1918" data-mp="2eaol41axo7w2">
              <h3 className="font-bold text-lg mb-2 text-gray-900" data-id="element-1919" data-mp="2eb83fj4hwyam">
                При отеке
              </h3>
              <p className="text-gray-600" data-id="element-1920" data-mp="29e7xr9bk5xk1">
                Приложите холодный компресс к щеке снаружи. Это уменьшит отек и
                боль. Не используйте тепло!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. OUR PROCESS */}
      <section className="py-16 bg-white" data-id="element-1921" data-mp="qnkb916cgli5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1922" data-mp="vflha5cx5fzq">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-1923" data-mp="vh5125xsa969">
            Как мы работаем в экстренных случаях
          </h2>
          <div className="grid md:grid-cols-4 gap-8" data-id="element-1924" data-mp="mn0bdpudz3sb">
            {[{
            step: '01',
            title: 'Связь',
            desc: 'Напишите нам в WhatsApp или позвоните. Опишите симптомы.'
          }, {
            step: '02',
            title: 'Оценка',
            desc: 'Мы оценим срочность и найдем ближайшее время для визита.'
          }, {
            step: '03',
            title: 'Визит',
            desc: 'Осмотр, рентген-диагностика и снятие острой боли.'
          }, {
            step: '04',
            title: 'План',
            desc: 'Если требуется дальнейшее лечение, мы составим план действий.'
          }].map((item, idx) => <Card key={idx} className="p-6 relative overflow-hidden" data-id="element-1925" data-mp="3dm65ymxesbxa" data-mpi={idx}>
                <div className="text-6xl font-bold text-red-50 absolute -top-4 -right-4 select-none" data-id="element-1926" data-mp="3rlfi9bihulvz">
                  {item.step}
                </div>
                <div className="relative z-10" data-id="element-1927" data-mp="3mnmd8lsxfxxa">
                  <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1928" data-mp="2ywqgda9m6cj6">
                    {item.title}
                  </h3>
                  <p className="text-gray-600" data-id="element-1929" data-mp="2uvy6qfwty5md">{item.desc}</p>
                </div>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 5. COMMON TREATMENTS */}
      <section className="py-16 bg-gray-50" data-id="element-1930" data-mp="33vl66w6011cs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1931" data-mp="33tqkvgqtf786">
          <div className="text-center mb-12" data-id="element-1932" data-mp="2g57ej6rbt4iq">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-1933" data-mp="2g3wb581slys5">
              Виды неотложной помощи
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1934" data-mp="2ouo3ajtrss3t">
            {[{
            title: 'Снятие острой боли',
            desc: 'Медикаментозная обработка и временная пломба.'
          }, {
            title: 'Удаление зуба',
            desc: 'Если зуб невозможно спасти или он является источником инфекции.'
          }, {
            title: 'Лечение каналов',
            desc: 'Удаление воспаленного нерва (пульпэктомия) для спасения зуба.'
          }, {
            title: 'Фиксация коронки',
            desc: 'Цементирование выпавшей коронки или моста.'
          }, {
            title: 'Вскрытие абсцесса',
            desc: 'Дренаж гноя для снятия отека и боли.'
          }, {
            title: 'Шинирование зубов',
            desc: 'Фиксация подвижных зубов после травмы.'
          }].map((item, idx) => <Card key={idx} className="p-6 bg-white" data-id="element-1935" data-mp="99ayc439lb2y" data-mpi={idx}>
                <Zap className="w-8 h-8 text-red-500 mb-4" data-id="element-1936" data-mp="4i19v6tgcdsn" />
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1937" data-mp="4ghs1bs5duor">
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-1938" data-mp="dth8yjvidp5">{item.desc}</p>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 6. FAQ */}
      <section className="py-16 bg-white" data-id="element-1939" data-mp="s62ew5rf9c3b">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1940" data-mp="o5s84qvt2hne">
          <div className="text-center mb-10" data-id="element-1941" data-mp="o6a0hcfckbq1">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-1942" data-mp="j6fbxfj1ny85">
              Частые вопросы
            </h2>
          </div>
          <Accordion items={faqs} data-id="element-1943" data-mp="e6j6rcym20r0" />
        </div>
      </section>

      {/* 7. INTERNAL LINKS */}
      <section className="py-12 bg-gray-50 border-t border-gray-100" data-id="element-1944" data-mp="33xxvh2asvu67">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1945" data-mp="2yy33swj7b0k3">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1946" data-mp="2yzwecmwxq4xd">
            Смотрите также
          </h2>
          <div className="flex flex-wrap gap-4" data-id="element-1947" data-mp="30cw74g3sjiy5">
            <Link to="/ru/russkij-stomatolog-london" data-id="element-1948" data-mp="35d0m21p1w5m9">
              <Button variant="outline" size="sm" data-id="element-1949" data-mp="35bphw0rp0vum">
                Русскоязычный стоматолог
              </Button>
            </Link>
            <Link to="/ru/lechenie-kanalov-london" data-id="element-1950" data-mp="2wclyswsj0gg4">
              <Button variant="outline" size="sm" data-id="element-1951" data-mp="2wfhvkhsn0z2z">
                Лечение каналов
              </Button>
            </Link>
            <Link to="/ru/services/extraction" data-id="element-1952" data-mp="2mf7l34xzm5ri">
              <Button variant="outline" size="sm" data-id="element-1953" data-mp="2mfzce3kjxhbz">
                Удаление зубов
              </Button>
            </Link>
            <Link to="/ru/prices" data-id="element-1954" data-mp="28lpzzfpnkkzd">
              <Button variant="outline" size="sm" data-id="element-1955" data-mp="2cowefamx5igx">
                Цены
              </Button>
            </Link>
            <Link to="/ru/contact" data-id="element-1956" data-mp="1ypd88ndo4h79">
              <Button variant="outline" size="sm" data-id="element-1957" data-mp="1yqeu7mw82o3r">
                Контакты
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 8. CTA (Urgent) */}
      <section className="py-16 bg-red-900" data-id="element-1958" data-mp="1k3g4bwd0k1r5">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-1959" data-mp="1f437cisfheau">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-1960" data-mp="1f5o1466x4t7n">
            Нужна срочная помощь?
          </h2>
          <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto" data-id="element-1961" data-mp="1tyrctwxtwcqg">
            Не ждите, пока боль усилится. Свяжитесь с нами прямо сейчас.
          </p>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-1962" data-mp="230hahkib07wo">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-1963" data-mp="1y38rxit8u1uw">
              <Button variant="primaryInverse" size="lg" className="w-full gap-2" data-id="element-1964" data-mp="2ctg4xj0heso7">
                <AlertCircle className="w-5 h-5" data-id="element-1965" data-mp="2ltbgvq1v2rpa" />
                Записаться на срочный приём
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-1966" data-mp="2holle2b47f4w">
              <Button variant="whatsappInverse" size="lg" className="w-full gap-2" data-id="element-1967" data-mp="3t3fv16we76my">
                <MessageCircle className="w-5 h-5" data-id="element-1968" data-mp="6016cjqbx5r4" />
                WhatsApp
              </Button>
            </a>
            <Button variant="outlineInverse" size="lg" className="w-full sm:w-auto gap-2" onClick={() => setIsCallbackOpen(true)} data-id="element-1969" data-mp="35fofzvjgwfhz">
              <Phone className="w-5 h-5" data-id="element-1970" data-mp="3efb5pk2u3wv7" />
              Заказать звонок
            </Button>
            <a href="tel:+447345197820" className="w-full sm:w-auto" data-id="element-1971" data-mp="3j6b6j0f2qz2z">
              <Button variant="outlineInverse" size="lg" className="w-full gap-2" data-id="element-1972" data-mp="3j8e3k4en9sh7">
                <Phone className="w-5 h-5" data-id="element-1973" data-mp="2qpgypj5752iv" />
                Позвонить
              </Button>
            </a>
            <Link to="/ru/prices#consultations" className="w-full sm:w-auto" data-id="element-1974" data-mp="2j5obole2p1ll">
              <Button variant="outlineInverse" size="lg" className="w-full" data-id="element-1975" data-mp="2e74o6gjtzhr4">
                Цены
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>;
}